import * as i1 from '@upr-web-primer/app-utils';
import { param, getValuesFromPath, genericRetryStrategy, AutoSubscription, AppEnvironmentService } from '@upr-web-primer/app-utils';
import * as i2 from '@angular/common/http';
import { provideHttpClient, withInterceptorsFromDi, HttpHeaders, HttpClient } from '@angular/common/http';
import * as i0 from '@angular/core';
import { NgModule, Injectable, Inject } from '@angular/core';
import { LocaleModule } from '@upr-web-primer/locale';
import { WindowModule, WINDOW } from '@upr-web-primer/window';
import { __decorate, __metadata } from 'tslib';
import * as i4 from '@angular/router';
import { Router } from '@angular/router';
import * as i5 from '@upr-web-primer/oidc';
import { OidcService } from '@upr-web-primer/oidc';
import * as i3 from '@upr-web-primer/storage';
import { LocalStorageService } from '@upr-web-primer/storage';
import { get, each, includes } from 'lodash-es';
import { DateTime } from 'luxon';
import { BehaviorSubject, ReplaySubject, firstValueFrom, timer } from 'rxjs';
import { retry } from 'rxjs/operators';
var TokenStatus;
(function (TokenStatus) {
  TokenStatus["Refresh"] = "refresh";
  TokenStatus["New"] = "new";
  TokenStatus["Reset"] = "reset";
  TokenStatus["Reset_Authn"] = "reset_authn";
})(TokenStatus || (TokenStatus = {}));
const DAL_REPLACED_DEFAULTS = {
  dal: {
    paths: {
      account: param('$dal.paths.account$'),
      content: param('$dal.paths.content$'),
      store: param('$dal.paths.store$'),
      dining: param('$dal.paths.dining$'),
      ticket_reservations: param('$dal.paths.ticket_reservations$'),
      foundation: param('$dal.paths.foundation$'),
      landing: param('$dal.paths.landing$'),
      hotels: param('$dal.paths.hotels$'),
      packages: param('$dal.paths.packages$'),
      hhn: param('$dal.paths.hhn$')
    }
  }
};
const DAL_ENV_DEFAULTS = {
  dal: {
    paths: {
      account: '/account',
      content: '/web',
      store: '/web-store',
      dining: '/dining',
      ticket_reservations: '/ticket-reservations',
      foundation: '/web-foundation',
      landing: '/landing',
      hotels: '/hotels',
      packages: '/packages',
      hhn: '/hhn'
    }
  }
};
class DalModule {
  static {
    this.ɵfac = function DalModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || DalModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: DalModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      providers: [provideHttpClient(withInterceptorsFromDi())],
      imports: [WindowModule, LocaleModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DalModule, [{
    type: NgModule,
    args: [{
      imports: [WindowModule, LocaleModule],
      providers: [provideHttpClient(withInterceptorsFromDi())]
    }]
  }], null, null);
})();
let DalService = class DalService {
  constructor(window, defaultValues, appEnvSrv, httpClient, localStorage, route, oidc) {
    this.window = window;
    this.defaultValues = defaultValues;
    this.appEnvSrv = appEnvSrv;
    this.httpClient = httpClient;
    this.localStorage = localStorage;
    this.route = route;
    this.oidc = oidc;
    this.cache = new Map();
    this.unregisteredUserPages = ['create-account', 'email-sign-up', 'email-unsubscribe', 'forgot-my-password', 'reset-my-password'];
    this.loggedInUserPagesPattern = '/account-management/';
    this._appTokenSet = new BehaviorSubject(TokenStatus.New);
    this.appEnvSrv.addDefaultValues(DAL_ENV_DEFAULTS, DAL_REPLACED_DEFAULTS);
    this.appTokenSet$ = this._appTokenSet.asObservable();
    this.subscriptions.add(this.appEnvSrv.envValues$.subscribe(values => this.envConfig = values['oidc']));
    this.envConfigDal = this.appEnvSrv.getEnvVariables('dal');
    this.setupConst();
    let redirected = this.localStorage.getItem('REDIRECTED_APP', true);
    let redirectedWait = this.localStorage.getItem('REDIRECTED_WAIT', true);
    const webview = this.isMobileAppWebViewInstance();
    const params = getValuesFromPath(this.window.location.href);
    const isNewLogin = get(params, 'id_token');
    // If user was navigated to account oidc page, but decided not to login, then we need to remove these values
    // Ignore if has new values from login
    const isLoggedIn = this.isLoggedIn();
    if (!isLoggedIn && !isNewLogin && (redirected || redirectedWait)) {
      redirected = false;
      redirectedWait = false;
      this.localStorage.removeItem('REDIRECTED_APP');
      this.localStorage.removeItem('REDIRECTED_WAIT');
    }
    if ((this.envConfig.enabled || this.envConfig.redirect_enabled || this.envConfig.authorize_enabled) && !redirected && !webview) {
      this.setOidcAppToken();
    }
    if (redirected && !redirectedWait && !webview) {
      this.updateAppToken(TokenStatus.Refresh);
    }
  }
  setupConst() {
    this.isLocalhost = new RegExp(/localhost|web-local/, 'gi').test(this.window.document.location.hostname);
    this.loginPage = this.getWebUrl(this.defaultValues.SIGN_IN);
    this.logOffPage = this.loginPage + '?signoff=true';
    if (!this.isLocalhost) {
      this.logOffPage = this.envConfigDal.paths.account + this.logOffPage;
    }
    this.baseUrl = this.envConfig.base_url;
    if (this.isLocalhost && (this.baseUrl || '') !== '/api') {
      //verify if path is already set in the environment loader for containerized app.
      const isApiBaseUrlAlreadySet = !this.baseUrl.includes('http');
      this.baseUrl = this.window.document.location.origin + (isApiBaseUrlAlreadySet ? this.baseUrl : '/api'); // this is for localhost
    }
    if (this.defaultValues.unregisteredUserPages) {
      this.unregisteredUserPages = this.defaultValues.unregisteredUserPages;
    }
    if (this.defaultValues.loggedInUserPagesPattern) {
      this.loggedInUserPagesPattern = this.defaultValues.loggedInUserPagesPattern;
    }
  }
  async httpRequest(relativeUrl, type, body, headers, timeoutDelay, isRetry, getResponseHeaders, gatewayUrl) {
    const hasHttp = relativeUrl.includes('http');
    const url = !!gatewayUrl && !hasHttp ? gatewayUrl + relativeUrl : hasHttp ? relativeUrl : this.baseUrl + relativeUrl;
    const cacheKey = type + '|' + url + '|' + JSON.stringify(body);
    const checkCache = this.cache.has(cacheKey);
    if (!checkCache) {
      const cachedObserver = new ReplaySubject(1);
      this.cache.set(cacheKey, cachedObserver);
      let httpHeaders = headers ? headers : new HttpHeaders();
      httpHeaders = httpHeaders.append('Content-Type', 'application/json');
      httpHeaders = this.addXUNIWebServiceHeaders(httpHeaders);
      const oAuthExpiresIn = this.localStorage.getItem('oAuthExpiresIn');
      const oAuthRefreshTokenExpiresIn = this.localStorage.getItem('oAuthRefreshTokenExpiresIn');
      const isNotExpiredOAuth = oAuthExpiresIn ? DateTime.fromISO(oAuthExpiresIn) >= DateTime.now() : false;
      const oAuthLogin = this.localStorage.getItem('oAuthLogin');
      const oAuthRefreshToken = this.localStorage.getItem('oAuthRefreshToken');
      const oAuthToken = this.localStorage.getItem('oAuthToken');
      const isLoggedIn = oAuthLogin === 'true' && oAuthRefreshToken && oAuthToken ? true : false;
      if (oAuthToken && isNotExpiredOAuth) {
        httpHeaders = this.appendOAuthAppHeaders(httpHeaders, oAuthToken || '');
        firstValueFrom(this.customHttpClient(type, url, body, httpHeaders, timeoutDelay, isRetry, getResponseHeaders)).catch(error => {
          return this.oidcErrorHandler(type, url, body, httpHeaders, isLoggedIn, error, timeoutDelay, isRetry, getResponseHeaders);
        }).then(data => cachedObserver.next(data)).catch(error => {
          this.cache.delete(cacheKey);
          cachedObserver.error(error);
        });
      } else {
        const refreshTokenExpired = oAuthRefreshTokenExpiresIn ? DateTime.now() >= DateTime.fromISO(oAuthRefreshTokenExpiresIn) : false;
        if (isLoggedIn && !refreshTokenExpired) {
          this.oidcRefreshLoginSession(type, url, body, httpHeaders, timeoutDelay, isRetry, getResponseHeaders).then(data => cachedObserver.next(data)).catch(error => {
            this.cache.delete(cacheKey);
            cachedObserver.error(error);
          });
        } else {
          this.getOAuthApp(type, url, body, httpHeaders, true, isLoggedIn, timeoutDelay, isRetry, getResponseHeaders).then(data => cachedObserver.next(data)).catch(error => {
            this.cache.delete(cacheKey);
            cachedObserver.error(error);
          });
        }
      }
      // Remove dynamically
      firstValueFrom(cachedObserver).finally(() => {
        firstValueFrom(timer(500)).then(() => {
          this.cache.delete(cacheKey);
          cachedObserver.complete();
        });
      });
      return firstValueFrom(cachedObserver);
    }
    return firstValueFrom(this.cache.get(cacheKey));
  }
  oAuthLogin(username, password, sourceId) {
    if (!username || !password) {
      return Promise.reject('username and password is mandatory for login');
    }
    this.setOAuthConfig(username, password, sourceId);
    return this.oidc.getAuthToken().then(response => {
      const access_token = get(response, 'access_token', {});
      try {
        const jwt = this.oidc.getDecodedAccessToken(access_token);
        this.localStorage.setItem('OAUTH_LOGIN_GID', get(jwt, 'sub', ''));
        this.localStorage.setItem('LOGGEDIN_GUEST_ID', get(jwt, 'sub', ''));
      } catch {
        this.localStorage.removeItem('OAUTH_LOGIN_GID');
        this.localStorage.removeItem('LOGGEDIN_GUEST_ID');
      }
      this.setOAuthTokens(true, response.access_token, response.refresh_token, response.expires_in, response.refresh_token_expires_in);
      return 'Login success';
    }).catch(errorResponse => {
      let errorMessage = '';
      const error = get(errorResponse, 'error');
      switch (error?.error) {
        case 'account_locked':
          errorMessage = 'Account locked';
          break;
        case 'invalid_client':
          errorMessage = 'Invalid username';
          break;
        default:
          errorMessage = 'Unknown error';
      }
      return Promise.reject(errorMessage);
    });
  }
  oidcRefreshLoginSession(type, url, body, headers, timeoutDelay, isRetry, getResponseHeaders) {
    const refreshToken = this.localStorage.getItem('oAuthRefreshToken');
    if (!refreshToken) {
      return Promise.reject('error on refresh token');
    }
    return this.oidc.getRefreshToken(refreshToken).then(response => {
      this.setOAuthTokens(true, response.access_token, response.refresh_token, response.expires_in, response.refresh_token_expires_in);
      headers = this.appendOAuthAppHeaders(headers, this.localStorage.getItem('oAuthToken') || '');
      return firstValueFrom(this.customHttpClient(type, url, body, headers, timeoutDelay, isRetry, getResponseHeaders));
    }).catch(error => {
      return this.oidcErrorHandler(type, url, body, headers, false, error, timeoutDelay, isRetry, getResponseHeaders);
    });
  }
  // this method would be useful for profile page or any other page
  // to set login session based on query string received on that page
  setOidcLoginSession(access_token, refresh_token, id_token, expires_in, refresh_expires_in) {
    this.localStorage.setItem('OIDC_ID_TOKEN', id_token);
    const jwt = this.oidc.getDecodedAccessToken(id_token);
    this.localStorage.setItem('OAUTH_LOGIN_GID', get(jwt, 'sub', ''));
    this.localStorage.setItem('LOGGEDIN_GUEST_ID', get(jwt, 'sub', ''));
    this.setOAuthTokens(true, access_token, refresh_token, expires_in, refresh_expires_in);
    const redirectedWait = this.localStorage.getItem('REDIRECTED_WAIT', true);
    if (redirectedWait) {
      // Clear authn first so next authn call is with user data
      this.updateAppToken(TokenStatus.Reset_Authn);
      this.updateAppToken(TokenStatus.Refresh);
      this.localStorage.removeItem('REDIRECTED_WAIT');
    }
  }
  async oAuthLogout(reset) {
    const id_token = this.localStorage.getItem('OIDC_ID_TOKEN');
    this.localStorage.setItem('REFRESH_TOKEN_FAIL', 'false');
    this.localStorage.removeItem('oAuthLogin');
    this.localStorage.removeItem('IS_SIGN_IN');
    this.localStorage.removeItem('OAUTH_LOGIN_GID');
    this.localStorage.removeItem('OIDC_ID_TOKEN');
    this.localStorage.removeItem('LOGGEDIN_GUEST_ID');
    this.localStorage.removeItem('oAuthToken');
    this.localStorage.removeItem('oAuthRefreshToken');
    this.localStorage.removeItem('oAuthExpiresIn');
    this.localStorage.removeItem('oAuthRefreshTokenExpiresIn');
    this.setOAuthConfig('', '');
    if (reset) {
      this.updateAppToken(TokenStatus.Reset);
    }
    const redirected = this.localStorage.getItem('REDIRECTED_APP', true);
    if (redirected) {
      this.localStorage.removeItem('REDIRECTED_APP');
      const current_path = this.window.location.href;
      return this.redirectToOidcAppEndSession(current_path, id_token);
    }
    return;
  }
  customHttpClient(type, url, body, headers, timeoutDelay, isRetry, getResponseHeaders) {
    const httpOptions = {
      headers: headers
    };
    if (!!getResponseHeaders) {
      httpOptions.observe = 'response';
    }
    let response;
    switch (type) {
      case 'GET':
        response = this.httpClient.get(url, httpOptions);
        break;
      case 'DELETE':
        if (body) {
          httpOptions['body'] = body;
        }
        response = this.httpClient.delete(url, httpOptions);
        break;
      case 'PUT':
        response = this.httpClient.put(url, body, httpOptions);
        break;
      case 'PATCH':
        response = this.httpClient.patch(url, body, httpOptions);
        break;
      case 'POST':
      default:
        response = this.httpClient.post(url, body, httpOptions);
    }
    const retryStrategy = genericRetryStrategy();
    if (timeoutDelay) {
      retryStrategy.delay = timeoutDelay;
    }
    if (!isRetry) {
      retryStrategy.count = 0;
    }
    return response.pipe(retry(retryStrategy));
  }
  addXUNIWebServiceHeaders(headers) {
    headers = headers.append('X-UNIWebService-ApiKey', this.defaultValues.UNIWebService_ApiKey || '');
    headers = headers.append('X-UNIWebService-AppVersion', this.defaultValues.UNIWebService_AppVersion || '');
    headers = headers.append('X-UNIWebService-Device', this.getBrowserName());
    headers = headers.append('X-UNIWebService-Platform', this.defaultValues.UNIWebService_Platform || 'Web');
    return headers;
  }
  getBrowserName() {
    if (this.localStorage.getItem('BROWSER_NAME')) {
      return this.localStorage.getItem('BROWSER_NAME') || '';
    }
    const userAgent = this.window.navigator.userAgent;
    const browsers = {
      Chrome: /chrome/i,
      Safari: /safari/i,
      Firefox: /firefox/i,
      IE: /internet explorer/i,
      Edge: /edge/i,
      Opera: /opera/i
    };
    let isBrowserIdentified = false;
    each(browsers, (regex, browser) => {
      if (regex.test(userAgent) && !isBrowserIdentified) {
        this.localStorage.setItem('BROWSER_NAME', browser);
        isBrowserIdentified = true;
        return;
      }
    });
    if (!isBrowserIdentified) {
      this.localStorage.setItem('BROWSER_NAME', 'unknown');
    }
    return this.localStorage.getItem('BROWSER_NAME') || '';
  }
  async oidcErrorHandler(type, url, body, headers, isLoggedIn, error, timeOut, isRetry, isGetResponseHeaders) {
    const refreshToken = this.localStorage.getItem('oAuthRefreshToken');
    const errorMessage = get(error, 'error.errors[0].errorKey') || get(error, 'error.message') || get(error, 'error.error') || get(error, 'error');
    let reAuth = false;
    // re-auth should land here if invalid grant was given, user would be logged in
    if (error?.status === 400 && errorMessage === 'invalid_grant') {
      // If under account, use account path
      if (!includes(this.window.location.pathname, this.defaultValues.SIGN_IN) && !includes(this.window.location.pathname, this.unregisteredUserPages) && includes(this.window.location.pathname, this.loggedInUserPagesPattern)) {
        // Let login page take care of signoff
        return this.route.navigateByUrl(this.logOffPage);
      } else {
        reAuth = true;
      }
    }
    // Capture for authn / bad login
    if (error?.status === 400 && errorMessage === 'invalid_client' || error?.status === 401 && errorMessage !== 'invalid_grant') {
      throw error;
    }
    // If unauthorized then reset authentication / if no obvious error, attempt a reset
    if (error?.status === 401 && errorMessage !== 'invalid_grant' || error?.status === 400 && !errorMessage) {
      // attempt refresh token call, if truly failed, it will do logout procedure
      if (isLoggedIn && refreshToken) {
        return this.oidcRefreshLoginSession(type, url, body, headers, timeOut, isRetry, isGetResponseHeaders);
      }
      reAuth = true;
    }
    if (reAuth) {
      if (isLoggedIn || refreshToken) {
        isLoggedIn = false;
        await this.oAuthLogout(true);
      } else {
        await this.oAuthLogout();
      }
      // Re-grab to setup next call attempt but make sure not to retry on this fail
      return this.getOAuthApp(type, url, body, headers, false, isLoggedIn, timeOut, isRetry, isGetResponseHeaders);
    }
    // regular errors should catch here
    throw error;
  }
  getOAuthApp(type, url, body, headers, retryOnFail, isLoggedIn, timeOut, isRetry, isGetResponseHeaders) {
    if (!this.envConfig.enabled && !this.envConfig.redirect_enabled) {
      return firstValueFrom(this.customHttpClient(type, url, body, headers, timeOut, isRetry, isGetResponseHeaders)).catch(error => {
        console.log(error);
        return Promise.reject();
      });
    } else {
      return this.oidc.getAppToken().then(response => {
        if (!response) {
          return;
        }
        this.setOAuthTokens(false, response.access_token, response.refresh_token, response.expires_in, response.refresh_token_expires_in);
        headers = this.appendOAuthAppHeaders(headers, this.localStorage.getItem('oAuthToken') || '');
        this.updateAppToken(TokenStatus.Refresh);
        return firstValueFrom(this.customHttpClient(type, url, body, headers, timeOut, isRetry, isGetResponseHeaders)).catch(error => {
          if (retryOnFail) {
            return this.oidcErrorHandler(type, url, body, headers, isLoggedIn, error, timeOut, isRetry, isGetResponseHeaders);
          }
          throw error;
        });
      });
    }
  }
  setOidcAppToken() {
    const oAuthLogin = this.localStorage.getItem('oAuthLogin');
    const oAuthRefreshToken = this.localStorage.getItem('oAuthRefreshToken');
    const oAuthToken = this.localStorage.getItem('oAuthToken');
    const isLoggedIn = oAuthLogin === 'true' && oAuthRefreshToken && oAuthToken ? true : false;
    const oAuthExpiresIn = this.localStorage.getItem('oAuthExpiresIn');
    const isNotExpiredOAuth = oAuthExpiresIn ? DateTime.fromISO(oAuthExpiresIn) >= DateTime.now() : false;
    if (oAuthToken && isNotExpiredOAuth || isLoggedIn) {
      this.updateAppToken(TokenStatus.Refresh);
      return Promise.resolve(true);
    }
    return this.oidc.getAppToken().then(response => {
      if (!response) {
        return Promise.resolve(false);
      }
      this.setOAuthTokens(false, response.access_token, response.refresh_token, response.expires_in, response.refresh_token_expires_in);
      this.updateAppToken(TokenStatus.Refresh);
      return Promise.resolve(true);
    });
  }
  appendOAuthAppHeaders(headers, oAuthToken) {
    let httpHeaders = headers ? headers : new HttpHeaders();
    if (!oAuthToken) {
      return httpHeaders;
    }
    const ibmClientHeader = httpHeaders && httpHeaders.has('X-IBM-Client-ID') ? httpHeaders.get('X-IBM-Client-ID') : '';
    const authorizationHeader = httpHeaders && httpHeaders.has('Authorization') ? httpHeaders.get('Authorization') : '';
    if (!ibmClientHeader) {
      httpHeaders = httpHeaders.set('X-IBM-Client-ID', this.envConfig.client_id);
    } else {
      httpHeaders = httpHeaders.set('X-IBM-Client-ID', this.envConfig.client_id);
    }
    httpHeaders = !authorizationHeader ? httpHeaders.set('Authorization', 'Bearer ' + oAuthToken) : httpHeaders.set('Authorization', 'Bearer ' + oAuthToken);
    return httpHeaders;
  }
  setOAuthConfig(username, password, sourceId) {
    this.localStorage.setItem('oAuthLoginUserName', username);
    this.localStorage.setItem('oAuthLoginUserPassword', password);
    let config = {
      username: username,
      password: password
    };
    config = !!sourceId ? Object.assign(config, {
      headers: {
        'x-source-id': sourceId || ''
      }
    }) : config;
    this.oidc.setConfig(config);
  }
  setOAuthTokens(oAuthLogin, oAuthToken, oAuthRefreshToken, oAuthExpiresIn, oAuthRefreshTokenExpiresIn) {
    const oAuthTokenExpires = DateTime.now().plus({
      seconds: oAuthExpiresIn
    }).toISO();
    this.localStorage.setItem('oAuthLogin', String(oAuthLogin));
    this.localStorage.setItem('IS_SIGN_IN', String(oAuthLogin));
    this.localStorage.setItem('oAuthToken', oAuthToken);
    this.localStorage.setItem('oAuthExpiresIn', oAuthTokenExpires);
    if (oAuthRefreshToken) {
      this.localStorage.setItem('oAuthRefreshToken', oAuthRefreshToken);
      const refreshTokenExpires = DateTime.now().plus({
        seconds: oAuthRefreshTokenExpiresIn || this.envConfig.refresh_expires_in_seconds
      }).toISO();
      this.localStorage.setItem('oAuthRefreshTokenExpiresIn', refreshTokenExpires);
    }
  }
  getUrlPrefix() {
    const groups = this.window.location.pathname.match(this.defaultValues.REGEX_ICE_URL_LOCALE);
    let country = this.defaultValues.DEFAULT_ROUTE_COUNTRY;
    let language = this.defaultValues.DEFAULT_ROUTE_LANGUAGE;
    if (groups) {
      country = get(groups, '5', country);
      language = get(groups, '4', language);
    }
    return '/' + language + '/' + country;
  }
  getWebUrl(url) {
    if (!url) {
      return '';
    }
    const prefix = this.getUrlPrefix();
    if (url.indexOf(prefix) < 0) {
      url = prefix + url;
    }
    return url;
  }
  redirectToOidcApp(redirectUrl, ignoreRedirect) {
    if (ignoreRedirect) {
      return;
    }
    const url = `${this.envConfig.base_url}${this.envConfig.redirect_url}?response_type=code id_token token&scope=openid default offline_access&client_id=${this.envConfig.client_id}&redirect_uri=${redirectUrl}&nonce=${Math.random().toString(36).substring(2) + Date.now().toString(36)}&state=web`;
    // For other sites to know to logout through oidc
    this.localStorage.setItem('REDIRECTED_APP', true);
    // For OIDC app to wait execution
    this.localStorage.setItem('REDIRECTED_WAIT', true);
    this.changeUrl(url);
  }
  async redirectToOidcAppEndSession(redirectUrl, idToken, ignoreRedirect) {
    const id_token_hint = idToken || this.localStorage.getItem('OIDC_ID_TOKEN');
    const url = `${this.envConfig.base_url}${this.envConfig.end_session}?id_token_hint=${id_token_hint}&post_logout_redirect_uri=${redirectUrl}`;
    if (!ignoreRedirect) {
      this.changeUrl(url);
    }
    return Promise.resolve();
  }
  changeUrl(url) {
    if (!url) {
      return;
    }
    this.window.location.href = url;
  }
  updateAppToken(status) {
    this._appTokenSet.next(status);
  }
  isLoggedIn() {
    const oAuthLogin = this.localStorage.getItem('oAuthLogin', true);
    const oAuthRefreshToken = this.localStorage.getItem('oAuthRefreshToken');
    const oAuthToken = this.localStorage.getItem('oAuthToken');
    return oAuthLogin && oAuthRefreshToken && oAuthToken ? true : false;
  }
  isMobileAppWebViewInstance() {
    const params = this.window.location?.search;
    const _params = !!params ? new URLSearchParams(params) : undefined;
    const getWebview = _params?.get('webview') || _params?.get('isMobile') || '';
    const isWebView = getWebview === 'true';
    const hasAndroidUniPlay = !!this.window?.AndroidUniPlay;
    const hasWebkitMessageHandlers = !!(this.window?.webkit && this.window?.webkit?.messageHandlers);
    return isWebView && (hasAndroidUniPlay || hasWebkitMessageHandlers);
  }
  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method, @typescript-eslint/no-empty-function
  ngOnDestroy() {
    // Needed for AutoSubscription
  }
  static {
    this.ɵfac = function DalService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || DalService)(i0.ɵɵinject(WINDOW), i0.ɵɵinject('DefaultValues'), i0.ɵɵinject(i1.AppEnvironmentService), i0.ɵɵinject(i2.HttpClient), i0.ɵɵinject(i3.LocalStorageService), i0.ɵɵinject(i4.Router), i0.ɵɵinject(i5.OidcService));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: DalService,
      factory: DalService.ɵfac,
      providedIn: 'root'
    });
  }
};
DalService = __decorate([AutoSubscription(), __metadata("design:paramtypes", [Object, Object, AppEnvironmentService, HttpClient, LocalStorageService, Router, OidcService])], DalService);
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DalService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: [WINDOW]
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['DefaultValues']
    }]
  }, {
    type: i1.AppEnvironmentService
  }, {
    type: i2.HttpClient
  }, {
    type: i3.LocalStorageService
  }, {
    type: i4.Router
  }, {
    type: i5.OidcService
  }], null);
})();

/*
 * Public API Surface of dal
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DAL_ENV_DEFAULTS, DAL_REPLACED_DEFAULTS, DalModule, DalService, TokenStatus };
